import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import StatisticsSegmentsItem from './StatisticsSegmentsItem';
import { statistics } from '../../../../config';

const defParamssegment = {
    exclusive: false,
    values: []
};

const StatisticsSegments = ({ segments: propSegments, onAction, onUpdateDate, loading }) => {
    const [segments, setSegments] = useState(propSegments);
    const [selected, setSelected] = useState([]);
    const [data, setData] = useState([]);
    const [isAction, setIsAction] = useState(false);

    const parseData = useCallback((segments) => {
        let list = [];
        let selected = [];

        for (let key in segments) {
            selected.push(key);
            list.push({
                ...segments[key],
                segment: key
            });
        }

        return {
            selected,
            list
        };
    }, []);

    // Initialize state ONCE; do not reinitialize on propSegments changes to avoid overwriting user edits
    useEffect(() => {
        const parseDataResult = parseData(propSegments);
        setSegments(propSegments);
        setSelected(parseDataResult.selected);
        setData(parseDataResult.list);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkAddEmptySegment = useCallback((nextData, nextSelected) => {
        const segment = '';
        const dataSegment = {
            ...defParamssegment,
            segment
        };
        let newData = Array.isArray(nextData) ? [...nextData] : [...data];
        let newSelected = Array.isArray(nextSelected) ? [...nextSelected] : [...selected];

        if (newSelected.length < statistics.groupParamsSegment.length && newSelected.indexOf('') === -1) {
            newData.push(dataSegment);
            newSelected.push(segment);

            setData(newData);
            setSelected(newSelected);
            return { newData, newSelected };
        }
        return { newData, newSelected };
    }, [data, selected]);

    const getNumberFilters = useCallback(() => {
        let number = selected.length;

        if (selected.indexOf('') !== -1) {
            number -= 1;
        }

        return number;
    }, [selected]);

    const buildSegmentsPayload = useCallback((list) => {
        const payload = {};
        (list || data).forEach((item) => {
            if (!item || !item.segment || !Array.isArray(item.values) || item.values.length === 0) return;
            payload[item.segment] = { exclusive: !!item.exclusive, values: item.values };
        });
        return payload;
    }, [data]);

    const onDeleteSegment = useCallback((index) => {
        let newData = [...data];
        let newSelected = [...selected];

        newData.splice(index, 1);
        newSelected.splice(index, 1);

        // Ensure we always have an empty row available
        const { newData: ensuredData, newSelected: ensuredSelected } = checkAddEmptySegment(newData, newSelected);

        setData(ensuredData);
        setSelected(ensuredSelected);
        
        setIsAction(true);
        setTimeout(() => {
            onAction(buildSegmentsPayload(ensuredData));
        }, 0);
    }, [data, selected, onAction, buildSegmentsPayload, checkAddEmptySegment]);

    const onUpdateDateCallback = useCallback(() => {
        onUpdateDate({ segments, selected, data });
    }, [onUpdateDate, segments, selected, data]);

    const onChangeSegment = useCallback((segmentData, index) => {
        let newData = [...data];
        let newSelected = [...selected];
        newData[index] = segmentData;
        newSelected[index] = segmentData.segment;
        setData(newData);
        setSelected(newSelected);
        setIsAction(true);
        setTimeout(() => {
            onAction(buildSegmentsPayload(newData));
            checkAddEmptySegment(newData, newSelected);
        }, 0);
    }, [data, selected, onAction, buildSegmentsPayload, checkAddEmptySegment]);

    const onActionCallback = useCallback(() => {
        setIsAction(true);
        onAction(buildSegmentsPayload());
    }, [onAction, buildSegmentsPayload]);

    // Optionally sync when propSegments changes AND user didn't touch yet
    useEffect(() => {
        if (isAction) { setIsAction(false); return; }
        if (segments.length === 0 && Object.keys(propSegments || {}).length) {
            const parseDataResult = parseData(propSegments);
            setSegments(propSegments);
            setSelected(parseDataResult.selected);
            setData(parseDataResult.list);
        }
    }, [propSegments, segments.length, isAction, parseData]);

    // Check add empty segment on mount and when data changes
    useEffect(() => {
        checkAddEmptySegment();
    }, [checkAddEmptySegment]);

    return (
        <div className="statistics__filter-wrapper">
            <div className="statistics__filter-items table-filter">
                {data.map((item, i) => (
                    <StatisticsSegmentsItem
                        key={`${item.segment || 'empty'}-${i}`}
                        index={i}
                        {...item}
                        selectedSegments={selected}
                        onDelete={onDeleteSegment}
                        onChangeSegment={onChangeSegment}
                        onUpdateDate={onUpdateDateCallback}
                        loading={loading}
                        totalFilters={getNumberFilters()}
                    />
                ))}
            </div>
        </div>
    );
};

StatisticsSegments.propTypes = {
    segments: PropTypes.object,
    onAction: PropTypes.func,
    onUpdateDate: PropTypes.func,
    loading: PropTypes.bool
};

StatisticsSegments.defaultProps = {
    segments: {},
    onAction: () => {},
    onUpdateDate: () => {},
    loading: false
};

export default StatisticsSegments;
